import { useState } from 'react';
import { PhotoViewerModal } from '../../../../components/photo-viewer/PhotoViewerModal';
import type { PostPhoto } from './types';

// A single photo is shown at its own aspect ratio, cropped only beyond this
// height so a tall screenshot doesn't fill the whole screen.
const SINGLE_PHOTO_MAX_HEIGHT = 480;

interface PostPhotoGridProps {
  photos: PostPhoto[];
}

// Grid template per number of photos: 1 full width, 2 side by side,
// 3 = one large on the left and two stacked on the right, 4 = 2×2.
// Several photos are cropped to fill the grid; a single one keeps its shape
// (see SINGLE_PHOTO_MAX_HEIGHT).
const GRID_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 grid-rows-2',
  4: 'grid-cols-2 grid-rows-2',
};

/**
 * Photos of a post, laid out in an adaptive grid. Tapping one opens the full-screen
 * viewer at that photo. Renders nothing without photos.
 */
export const PostPhotoGrid = ({ photos }: PostPhotoGridProps) => {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  if (photos.length === 0) return null;
  const single = photos.length === 1;

  // Stop propagation so a tap inside a PostCard does not also open the post detail
  const open = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    setViewerIndex(index);
  };

  return (
    <>
      <div
        className={`mt-2.5 grid gap-[2px] overflow-hidden bg-[#e4e8f0] ${GRID_CLASS[Math.min(photos.length, 4)]}`}
        style={{ borderRadius: '12px', aspectRatio: single ? undefined : '16 / 9' }}
        data-testid="post-photo-grid"
        data-count={photos.length}
      >
        {photos.map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            className={`relative h-full w-full overflow-hidden bg-transparent ${
              photos.length === 3 && index === 0 ? 'row-span-2' : ''
            }`}
            style={{ padding: 0 }}
            onClick={(event) => open(event, index)}
            aria-label={`Ver foto ${index + 1} de ${photos.length}`}
          >
            <img
              src={photo.url}
              alt=""
              loading="lazy"
              className={single ? 'block h-auto w-full object-cover' : 'h-full w-full object-cover'}
              style={single ? { maxHeight: SINGLE_PHOTO_MAX_HEIGHT } : undefined}
              draggable={false}
            />
          </button>
        ))}
      </div>

      {/* Mounted only while open, so a feed doesn't keep one modal per post. The
          modal's events bubble through the React tree: keep them from reaching the PostCard */}
      {viewerIndex !== null && (
        <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <PhotoViewerModal photos={photos} initialIndex={viewerIndex} onClose={() => setViewerIndex(null)} />
        </div>
      )}
    </>
  );
};
